import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, createRef, useState } from "react";
import styled from "@emotion/styled";
import { compare } from "natural-orderby";
import {
    filter,
    find,
    forEach,
    isEmpty,
    map,
    reduce,
    snakeCase,
    last,
} from "lodash";

import IconButton from "~/components/buttons/IconButton";

import Row from "~/components/layout/Row";
import Col from "~/components/layout/Col";

import DropdownFilter from "~/components/DropdownFilter";
import ErrorBoundary from "~/components/ErrorBoundary";

import useStringTranslation from "~/hooks/translations/useStringTranslation";

import { useLocationContext } from "~/context/LocationContext";

import { screenReaderText } from "~/styles/screenReaderText";

import {
    handleProductFilterChange,
    removeInvalidOptions,
    formatFilterOptions,
    getDropdownFilterOptions,
    handleUrlStateChange,
} from "~/utilities/filters";

import { removeHtml, sanitizeAndParseHTML } from "~/utilities/helpers";

const MAX_LENGTH = 19;

const FilterSelectionWrapper = styled.li`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    height: auto;
    @media (min-width: ${({ theme }) =>
            theme.table.breakpoints.filterColumnWrap}) {
        min-width: min-content;
        max-width: fit-content;
        max-height: 70px;
    }
`;
const FilterSelectionList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    flex-direction: row;
    @media (max-width: ${({ theme }) =>
            theme.table.breakpoints.filterColumnWrap}) {
        flex-direction: row;
        width: 100%;
    }
`;

const AppliedFilterSelectionList = styled.ul`
    display: none;
    flex-wrap: wrap;
    flex-direction: row;
    @media (min-width: ${({ theme }) =>
            theme.table.breakpoints.filterColumnWrap}) {
        display: flex;
    }
`;

const FilterSelectionItem = styled.li``;

const RemoveFiltersList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    margin: 15px 0;
    @media (min-width: ${({ theme }) =>
            theme.table.breakpoints.filterColumnWrap}) {
        display: none;
    }
`;

const RemoveFiltersRow = styled(Row)`
    margin-left: 1px;
    @media (max-width: ${({ theme }) =>
            theme.table.breakpoints.filterColumnWrap}) {
        display: none;
    }
`;

const FilterSelectionButton = styled.button`
    display: flex;
    align-items: center;
    padding: 0.5rem 1.25rem;
    margin: 0.8rem 1rem 0 0;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.palette.grayFive};
    transition:
        background-color 200ms ease-in-out,
        color 200ms ease-in-out;

    &:hover {
        background-color: ${({ theme }) => theme.palette.teal};
        color: ${({ theme }) => theme.palette.white};
    }

    &:hover .icon {
        color: ${({ theme }) => theme.palette.white};
    }
`;

const FilterSelectionText = styled.span`
    font-size: 1.72rem;
    margin-right: 1.4rem;
`;

const FilterSelectionIcon = styled.span`
    color: ${({ theme }) => theme.palette.purple};
    font-weight: bolder;
    font-size: 1.4rem;
    transition:
        background-color 200ms ease-in-out,
        color 200ms ease-in-out;
`;

const FilterSelectionCol = styled(Col)`
    display: flex;
    flex-direction: row;
    @media (max-width: ${({ theme }) =>
            theme.table.breakpoints.filterColumnWrap}) {
        width: 100% !important;
        display: flex;
    }
`;

const ClearAllCol = styled(Col)`
    align-self: start;
    margin-top: ${({ longestLabelLength }) =>
        longestLabelLength > MAX_LENGTH ? "1.7rem" : ".60rem"};
    @media (max-width: ${({ theme }) =>
            theme.table.breakpoints.filterColumnWrap}) {
        width: 100% !important;
        display: flex;
    }
`;

export const ZeroProducts = styled.span`
    font-weight: bold;
    margin-bottom: 2rem;
    display: inline-block;
`;

const ScreenReaderTextLevel3 = styled.h3`
    ${screenReaderText}
`;

const ScreenReaderTextLevel4 = ScreenReaderTextLevel3.withComponent("h4");

const ScreenReaderTextLevel5 = ScreenReaderTextLevel3.withComponent("h5");

const AppliedFilters = ({
    handleFilter,
    numberOfFiltersApplied,
    appliedFilters,
    appliedFilterRefs,
    lastFilter,
}) => {
    const { appliedFiltersText, removeFilterText } = useStringTranslation([
        { text: "Applied Filters", handle: "filtersAppliedText" },
        { text: "Remove Filter", handle: "removeFilterText" },
    ]);

    return numberOfFiltersApplied && !isEmpty(appliedFilters?.filters) ? (
        <RemoveFiltersRow halfGutter grow>
            <ScreenReaderTextLevel4 id="SRTAppliedFilters">
                {appliedFiltersText}
            </ScreenReaderTextLevel4>
            <AppliedFilterSelectionList aria-labelledby="SRTAppliedFilters">
                {map(appliedFilters?.filters, ({ id, filter }, index) => {
                    const filterId = snakeCase(`${id}-${filter}`);

                    return (
                        <FilterSelectionItem key={filterId}>
                            <FilterSelectionButton
                                className="selectedFilterItem"
                                id={filterId}
                                onClick={() => {
                                    const lastAppliedFilter =
                                        appliedFilterRefs[index - 1];
                                    if (
                                        lastAppliedFilter &&
                                        lastAppliedFilter.current
                                    ) {
                                        lastAppliedFilter.current.focus();
                                    } else {
                                        lastFilter.current.firstElementChild.focus();
                                    }
                                    handleFilter(id, filter);
                                }}
                                ref={appliedFilterRefs[index]}
                            >
                                <ScreenReaderTextLevel5>
                                    {removeFilterText}
                                </ScreenReaderTextLevel5>
                                <FilterSelectionText>
                                    {sanitizeAndParseHTML(filter)}
                                </FilterSelectionText>
                                <FilterSelectionIcon className="icon">
                                    &#10005;
                                </FilterSelectionIcon>
                            </FilterSelectionButton>
                        </FilterSelectionItem>
                    );
                })}
            </AppliedFilterSelectionList>
        </RemoveFiltersRow>
    ) : null;
};

const MobileAppliedFilters = ({
    numberOfFiltersApplied,
    tableState,
    filter,
    handleFilter,
    handleFocusAdjust,
}) => {
    if (numberOfFiltersApplied === 0) {
        return null;
    }

    return map(tableState.columnFilters, ({ id, value }) => {
        if (id !== filter.name) {
            return null;
        }

        value.sort((a, b) => compare()(a.value, b.value));

        return (
            <RemoveFiltersList key={`${value}-${id}`}>
                {map(value, (val, valueIndex) => {
                    const filterId = snakeCase(`${id}-${valueIndex}-${val}`);

                    return (
                        <FilterSelectionItem key={filterId}>
                            <FilterSelectionButton
                                id={filterId}
                                className="selectedFilterItemMobile"
                                onClick={() => {
                                    handleFilter(id, val);
                                    handleFocusAdjust(filterId, true);
                                }}
                            >
                                <ScreenReaderTextLevel5>
                                    Remove filter
                                </ScreenReaderTextLevel5>
                                <FilterSelectionText>
                                    {sanitizeAndParseHTML(val)}
                                </FilterSelectionText>
                                <FilterSelectionIcon className="icon">
                                    &#10005;
                                </FilterSelectionIcon>
                            </FilterSelectionButton>
                        </FilterSelectionItem>
                    );
                })}
            </RemoveFiltersList>
        );
    });
};

const FilterList = ({
    formattedFilterOptions,
    tableState,
    languageCode,
    numberOfFiltersApplied,
    handleFilter,
    filterRefs,
    longestLabelLength,
    handleFocusAdjust,
    mobileAppliedFilterRefs,
}) => {
    return (
        <FilterSelectionCol flex flexColumn halfGutter xs="auto">
            <FilterSelectionList aria-labelledby="SRTFilterBy">
                {formattedFilterOptions.map((filter, index) => {
                    if (isEmpty(filter.options)) {
                        return null;
                    }

                    const selectedOptions =
                        find(tableState.columnFilters, { id: filter.name })
                            ?.value || [];

                    filter.options.sort((a, b) => compare()(a.value, b.value));

                    return (
                        <FilterSelectionWrapper
                            key={filter.name}
                            data-lastfilter={
                                index === formattedFilterOptions.length - 1
                                    ? "true"
                                    : null
                            }
                        >
                            <DropdownFilter
                                {...{
                                    selectedOptions,
                                    languageCode,
                                }}
                                ref={filterRefs.current[index]}
                                label={filter.label}
                                showAsCheckbox={true}
                                minHeight={
                                    longestLabelLength > MAX_LENGTH
                                        ? "7rem"
                                        : "auto"
                                }
                                options={getDropdownFilterOptions(
                                    filter,
                                    handleFilter,
                                )}
                                onLastOptionBlur={() => {
                                    const nextFilter =
                                        filterRefs.current[index + 1];
                                    if (nextFilter) {
                                        nextFilter.current.firstElementChild.focus();
                                    }
                                }}
                            />
                            <MobileAppliedFilters
                                {...{
                                    numberOfFiltersApplied,
                                    tableState,
                                    filter,
                                    handleFilter,
                                    handleFocusAdjust,
                                }}
                                appliedFilterRefs={
                                    mobileAppliedFilterRefs[filter.name]
                                }
                            />
                        </FilterSelectionWrapper>
                    );
                })}
            </FilterSelectionList>
        </FilterSelectionCol>
    );
};

const ClearAllFiltersButton = ({
    longestLabelLength,
    resetFilters,
    children,
    numberOfFiltersApplied,
}) => {
    if (numberOfFiltersApplied === 0) {
        return null;
    }

    return (
        <ClearAllCol
            longestLabelLength={longestLabelLength}
            flex
            halfGutter
            xs="auto"
        >
            <IconButton icon={"times"} onClick={() => resetFilters()}>
                {children}
            </IconButton>
        </ClearAllCol>
    );
};

const ProductTableFiltersSelection = ({
    ariaLabel,
    filterOptions = [],
    headers = [],
    languageCode,
    setAllFilters,
    setFilter,
    tableRows,
    tableState,
    isRefDesign = false,
    currentTab = "",
}) => {
    const { location } = useLocationContext();
    const [allTableRows, setAllTableRows] = useState([]);
    const focusRef = useRef();
    const filterRefs = useRef(map(filterOptions, () => createRef()));

    const formattedFilterOptions = useMemo(() => {
        const validOptions = removeInvalidOptions(filterOptions, headers);
        return formatFilterOptions(
            validOptions,
            tableState.columnFilters,
            isRefDesign ? tableRows : allTableRows,
        );
    }, [
        tableState.columnFilters,
        filterOptions,
        headers,
        allTableRows,
        isRefDesign,
        tableRows,
    ]);

    useEffect(() => {
        if (allTableRows.length === 0) {
            setAllTableRows(tableRows);
        }
    }, [allTableRows, tableRows]);

    const longestLabelLength = reduce(
        formattedFilterOptions,
        (maxLen, option) => {
            const optionLabel = removeHtml(option.label);
            if (optionLabel.trim().length > maxLen) {
                return optionLabel.trim().length;
            }
            return maxLen;
        },
        0,
    );

    const handleFilter = (filterName, filterValue) => {
        if (filterName) {
            handleProductFilterChange(
                tableState,
                filterName,
                filterValue,
                location,
                setFilter,
                setAllFilters,
                currentTab,
            );
        } else {
            resetFilters();
        }
    };

    const handleFocusAdjust = (id, isMobile = false) => {
        const reFocus = () => {
            if (focusRef.current) {
                const element = focusRef.current;
                element.querySelector("[data-lastfilter]>*>button")?.focus();
            }
        };

        const findAllFilters = () => {
            if (!focusRef.current) {
                return [];
            }

            const filterSelector = isMobile
                ? ".selectedFilterItemMobile"
                : ".selectedFilterItem";

            const element = focusRef.current;
            return Array.from(element.querySelectorAll(filterSelector));
        };

        if (id === "CLEAR_ALL") {
            reFocus();
            return;
        }

        const elements = filter(
            findAllFilters(),
            (element) => element.id !== id,
        );

        if (isEmpty(elements)) {
            reFocus();
        } else {
            const element = elements[elements.length - 1];
            element?.focus();
        }
    };

    const resetFilters = () => {
        setAllFilters([]);
        handleUrlStateChange([], currentTab, location.pathname);
        handleFocusAdjust("CLEAR_ALL");
    };

    const numberOfFiltersApplied = reduce(
        tableState.columnFilters,
        (acc, el) => {
            return acc + el.value.length;
        },
        0,
    );

    const appliedFilters = useMemo(() => {
        let filters = [];
        let refs = [];
        let mobileRefs = {};
        const tableStateFilters = tableState.columnFilters;

        tableStateFilters.sort((a, b) => {
            const indexOfA = formattedFilterOptions.indexOf(
                formattedFilterOptions.find((element) => element.name === a.id),
            );
            const indexOfB = formattedFilterOptions.indexOf(
                formattedFilterOptions.find((element) => element.name === b.id),
            );
            return indexOfA - indexOfB;
        });

        forEach(tableStateFilters, ({ value, id }) => {
            mobileRefs[id] = [];
            forEach(value, (filter) => {
                filters.push({ id, filter });
                refs.push(createRef());
                mobileRefs[id].push(createRef());
            });
        });
        return {
            filters,
            refs,
            mobileRefs,
        };
    }, [tableState.columnFilters, formattedFilterOptions]);

    if (isEmpty(formattedFilterOptions)) {
        return null;
    }

    return (
        <ErrorBoundary>
            <div ref={focusRef}>
                <ScreenReaderTextLevel3 id="SRTFilterBy">
                    {ariaLabel} - Filter By
                </ScreenReaderTextLevel3>
                <Row halfGutter grow>
                    <ErrorBoundary>
                        <FilterList
                            {...{
                                formattedFilterOptions,
                                tableState,
                                languageCode,
                                numberOfFiltersApplied,
                                handleFilter,
                                filterRefs,
                                longestLabelLength,
                                handleFocusAdjust,
                            }}
                            mobileAppliedFilterRefs={appliedFilters.mobileRefs}
                        />
                    </ErrorBoundary>
                    <ClearAllFiltersButton
                        {...{
                            longestLabelLength,
                            resetFilters,
                            numberOfFiltersApplied,
                        }}
                    >
                        Clear All Filters
                    </ClearAllFiltersButton>
                </Row>
                <AppliedFilters
                    {...{
                        tableState,
                        handleFilter,
                        numberOfFiltersApplied,
                        formattedFilterOptions,
                    }}
                    appliedFilters={appliedFilters}
                    appliedFilterRefs={appliedFilters.refs}
                    lastFilter={last(filterRefs.current)}
                />
                {isEmpty(tableRows) ? (
                    <Row halfGutter noWrap="md">
                        <Col>
                            <ZeroProducts>0 Products</ZeroProducts>
                        </Col>
                    </Row>
                ) : null}
            </div>
        </ErrorBoundary>
    );
};

ProductTableFiltersSelection.propTypes = {
    tableRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    tableState: PropTypes.shape({}).isRequired,
    filterOptions: PropTypes.arrayOf(PropTypes.shape({})),
    headers: PropTypes.arrayOf(PropTypes.string),
    setAllFilters: PropTypes.func.isRequired,
    setFilter: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string.isRequired,
    isRefDesign: PropTypes.bool,
    currentTab: PropTypes.string,
};

export default ProductTableFiltersSelection;
